import { createFeatureSelector, createSelector } from '@ngrx/store'
import { IShowState} from './show.reducer'
import { StoreKeysEnum } from '../../../../../store/store-keys'
import { toDisplayUnits } from '../../../../../shared/utils/month-year-unit'

export const selectContractShowFeature = createFeatureSelector<Readonly<IShowState>>(StoreKeysEnum.ContractShowFeature)

export const selectContractShowContract = createSelector(
    selectContractShowFeature,
    feature => (feature.contract)
)

export const selectContractShowContractId = createSelector(
    selectContractShowContract,
    contract => (contract?.id)
)

export const selectContractShowContractToEconomicYear = createSelector(
    selectContractShowContract,
    contract => (contract?.toEconomicYear)
)

export const selectContractShowContractSignatureDate = createSelector(
    selectContractShowContract,
    contract => (contract?.signatureDate)
)

export const selectContractShowContractOwners = createSelector(
    selectContractShowContract,
    contract => (contract?.owners)
)

export const selectContractShowContractOwnersLength = createSelector(
    selectContractShowContract,
    contract => (contract?.owners?.length)
)

export const selectContractShowContractVersionType= createSelector(
    selectContractShowContract,
    contract => (contract?.versionType)
)

export const selectContractShowParcels = createSelector(
    selectContractShowFeature,
    feature => (feature.parcels)
)

// Payments
export const selectContractShowOutgoingPayments = createSelector(
    selectContractShowFeature,
    feature => (feature.outgoingPayments)
)
export const selectContractShowIncomingPayments = createSelector(
    selectContractShowFeature,
    feature => (feature.incomingPayments)
)

export const selectContractShowCurrentYearPaymentReady = createSelector(
    selectContractShowFeature,
    feature => (feature.currentYearPaymentReady)
)
export const selectContractShowParcelsOrder = createSelector(
    selectContractShowFeature,
    feature => (feature.parcelsOrder),
)
export const selectContractShowPayments = createSelector(
    selectContractShowOutgoingPayments,
    selectContractShowIncomingPayments,
    selectContractShowCurrentYearPaymentReady,
    (outgoingPayments, incomingPayments, currentYearPaymentReady) => ({
        outgoingPayments,
        incomingPayments,
        currentYearPaymentReady
    })
)
// Attachments
export const selectContractShowAttachments = createSelector(
    selectContractShowFeature,
    feature => (feature.attachments)
)
export const selectContractShowAttachmentsLoading = createSelector(
    selectContractShowFeature,
    feature => (feature.attachmentsLoading)
)
export const selectContractShowAttachmentsState = createSelector(
    selectContractShowAttachments,
    selectContractShowAttachmentsLoading,
    (attachments, attachmentsLoading) => ({
        attachments,
        attachmentsLoading,
    })
)
// Rest
export const selectContractShowAllowEdit = createSelector(
    selectContractShowFeature,
    feature => (feature.allowEdit)
)
export const selectContractShowIsAppendix = createSelector(
    selectContractShowFeature,
    feature => (feature.isAppendix)
)
export const selectContractShowRelatedContracts = createSelector(
    selectContractShowFeature,
    feature => (feature.relatedContracts)
)
export const selectContractShowSameLVAndKatuze = createSelector(
    selectContractShowFeature,
    feature => (feature.sameLVAndKatuze)
)
export const selectContractShowSignatureDocuments = createSelector(
    selectContractShowFeature,
    feature => (feature.signatureDocuments)
)
export const selectContractShowSignatureDocumentsGroup = createSelector(
    selectContractShowFeature,
    feature => (feature.signatureDocumentsGroup)
)
export const selectContractShowSuccessiveContractAvailable = createSelector(
    selectContractShowFeature,
    feature => (feature.successiveContractAvailable)
)
export const selectContractShowState = createSelector(
    selectContractShowAllowEdit,
    selectContractShowIsAppendix,
    selectContractShowRelatedContracts,
    selectContractShowSameLVAndKatuze,
    selectContractShowSignatureDocuments,
    selectContractShowSuccessiveContractAvailable,
    selectContractShowParcelsOrder,
    (allowEdit, isAppendix, relatedContracts, sameLVAndKatuze, signatureDocuments, successiveContractAvailable, parcelsOrder) => ({
        allowEdit,
        isAppendix,
        relatedContracts,
        sameLVAndKatuze,
        signatureDocuments,
        successiveContractAvailable,
        parcelsOrder,
    })
)
// EndDialogComponent
export const selectContractShowEndDialogState = createSelector(
    selectContractShowContract,
    (contract) => ({
        object: contract,
        efficiencyFromDate: contract?.signatureDate,
        endType: contract?.endType,
        endDate: contract?.endDate,
        resignDate: contract?.resignDate,
        expirationDate: contract?.expirationDate,
    })
)
// notice-period
export const selectContractShowContractNoticePeriod = createSelector(
    selectContractShowContract,
    (contract) => ({
        noticePeriodMonths: toDisplayUnits(contract?.noticePeriodMonths, contract?.noticePeriodMonthsUnit),
        noticePeriodMonthsUnit: contract?.noticePeriodMonthsUnit
    })
)
// Contract documents
export const selectContractShowDocumentsState = createSelector(
    selectContractShowRelatedContracts,
    selectContractShowSignatureDocuments,
    selectContractShowSignatureDocumentsGroup,
    selectContractShowContract,
    selectContractShowAttachments,
    selectContractShowAttachmentsLoading,
    (relatedContracts, signatureDocuments, signatureDocumentsGroup, contract, attachments, attachmentsLoading) => ({
        relatedContracts,
        signatureDocuments,
        signatureDocumentsGroup,
        owners: contract?.owners,
        contractId: contract?.id,
        payoutToId: undefined,
        groupId: contract?.groupId,
        createIndividualDocuments: contract?.createIndividualDocuments,
        canAddDocuments: true,
        attachments,
        attachmentsLoading,
    })
)
// DurationPeriodComponent
export const selectContractShowDurationPeriodState = createSelector(
    selectContractShowContract,
    (contract) => ({
        indefinite: contract?.indefinite,
        reusedPreviousExpiration: contract?.reusedPreviousExpiration,
        durationMonths: toDisplayUnits(contract?.durationMonths, contract?.durationMonthsUnit),
        durationMonthsUnit: contract?.durationMonthsUnit,
        durationToDate: contract?.durationToDate,
    })
)
export const selectContractShowNoteState = createSelector(
    selectContractShowContract,
    (contract) => ({
        objectId: contract.id,
        note: contract.note
    })
)

export const selectContractShowLvInfo = createSelector(
    selectContractShowParcels,
    selectContractShowSameLVAndKatuze,
    (parcels, sameLVAndKatuze) => {
        if (sameLVAndKatuze && parcels?.length) {
            return parcels[0].katuzeNazev + ', LV ' + parcels[0].lv
        } else {
            return null
        }
    }
)

export const selectContractShowOptionalOptions = createSelector(
    selectContractShowContract,
    (contract) => ({
        furtherRentPossible: contract.furtherRentPossible,
        reqNewAppendixAfterReprice: contract.reqNewAppendixAfterReprice,
        tenantHasPreemption: contract.tenantHasPreemption
    })
)

export const selectContractShowPaymentsFromEconomicYear = createSelector(
    selectContractShowContract,
    contract => (contract?.paymentsFromEconomicYear),
)